import React, { useCallback, useEffect, useState } from 'react';

type InteractionState = {
  hover: boolean;
  focus: boolean;
  focusWithin: boolean;
  active: boolean;
};

const defaultState: InteractionState = {
  hover: false,
  focus: false,
  focusWithin: false,
  active: false,
};

function useInteraction<TElement extends HTMLElement = HTMLElement>(): [
  React.RefCallback<TElement>,
  InteractionState,
] {
  const [element, setElement] = useState<TElement | null>(null);
  const [state, setState] = useState(defaultState);

  const ref = useCallback((node: TElement | null) => {
    setElement(node);
  }, []);

  useEffect(() => {
    if (!element) return;

    const set = (value: Partial<InteractionState>) => {
      setState((current) => ({ ...current, ...value }));
    };

    const handleMouseEnter = () => set({ hover: true });
    const handleMouseLeave = () => set({ hover: false, active: false });
    const handleFocusIn = (event: FocusEvent) => {
      set({
        focus: event.target === element,
        focusWithin: true,
      });
    };
    const handleFocusOut = (event: FocusEvent) => {
      const nextTarget = event.relatedTarget as Node | null;
      set({
        focus: false,
        focusWithin: !!nextTarget && element.contains(nextTarget),
      });
    };
    const handleActiveStart = () => set({ active: true });
    const handleActiveEnd = () => set({ active: false });

    element.addEventListener('mouseenter', handleMouseEnter);
    element.addEventListener('mouseleave', handleMouseLeave);
    element.addEventListener('focusin', handleFocusIn);
    element.addEventListener('focusout', handleFocusOut);
    element.addEventListener('mousedown', handleActiveStart);
    element.addEventListener('touchstart', handleActiveStart);
    window.addEventListener('mouseup', handleActiveEnd);
    window.addEventListener('touchend', handleActiveEnd);
    window.addEventListener('touchcancel', handleActiveEnd);
    window.addEventListener('blur', handleActiveEnd);

    return () => {
      element.removeEventListener('mouseenter', handleMouseEnter);
      element.removeEventListener('mouseleave', handleMouseLeave);
      element.removeEventListener('focusin', handleFocusIn);
      element.removeEventListener('focusout', handleFocusOut);
      element.removeEventListener('mousedown', handleActiveStart);
      element.removeEventListener('touchstart', handleActiveStart);
      window.removeEventListener('mouseup', handleActiveEnd);
      window.removeEventListener('touchend', handleActiveEnd);
      window.removeEventListener('touchcancel', handleActiveEnd);
      window.removeEventListener('blur', handleActiveEnd);
    };
  }, [element]);

  return [ref, state];
}

export default useInteraction;
